import { Trans, useLingui } from '@lingui/react/macro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import type { ActiveDashboard } from '@/utils/session/dashboard-from-session';
import type {
  MembershipOrganization,
  MembershipTeam,
} from '@/utils/session/fetch-researcher-memberships';

import { AlertFloating } from '@/components/application/alerts/alerts';
import {
  Button,
  ExternalLinkButton,
  LinkButton,
} from '@/components/base/buttons/button';
import { Select } from '@/components/base/select/select';
import { BASE_PATH } from '@/constants/config';
import { queryKeys } from '@/hooks/api';
import { AuthLayout } from '@/routes/participants/-components/auth-layout';
import { ResearcherRightPanel } from '@/routes/researchers/-auth/right-panel';
import { getSafeReturnTo } from '@/utils/get-safe-return-to';
import { fetchResearcherMemberships } from '@/utils/session/fetch-researcher-memberships';
import { persistActiveDashboard } from '@/utils/session/persist-active-dashboard';
import {
  decideWorkspaceOptions,
  type WorkspaceOptions,
} from '@/utils/session/workspace-options';

const searchSchema = z.object({
  returnTo: z.string().max(2048).optional(),
});

function failureMessage(
  reason:
    | 'unauthenticated'
    | 'forbidden-role'
    | 'forbidden-org'
    | 'forbidden-team',
  defaultMessage: string,
): string {
  switch (reason) {
    case 'unauthenticated':
      return 'Your session has expired. Please sign in again.';
    case 'forbidden-role':
      return 'Only researcher accounts can pick an active team.';
    case 'forbidden-org':
      return 'You are no longer a member of that organization.';
    case 'forbidden-team':
      return 'You are no longer a member of that team.';
    default:
      return defaultMessage;
  }
}

export const Route = createFileRoute('/researchers/select-team')({
  validateSearch: searchSchema,
  beforeLoad: async ({
    context,
  }): Promise<{ workspaces: WorkspaceOptions }> => {
    const { user } = context;
    if (!user) {
      throw redirect({ to: '/researchers/login' });
    }
    if (!user.roles.includes('researcher')) {
      throw redirect({ to: '/participants' });
    }
    if (!user.isEmailVerified) {
      throw redirect({ to: '/researchers/verify-email' });
    }

    if (!user.organizations?.length) {
      throw redirect({ to: '/researchers/onboarding' });
    }

    const result = await fetchResearcherMemberships({
      data: { userId: user.id },
    });

    const workspaces = decideWorkspaceOptions(result, user.teams ?? []);
    if (workspaces.status === 'no-organizations') {
      throw redirect({ to: '/researchers/onboarding' });
    }

    return { workspaces };
  },
  component: SelectTeamPage,
});

interface Selection {
  orgId: string;
  teamId: string;
}

/**
 * Where the radio starts. The session's current workspace when there is one —
 * arriving here with a team already chosen means "switch team", and the row
 * you are on is the obvious anchor. Otherwise the first org that has a team.
 */
function defaultSelection(
  memberships: MembershipOrganization[],
  activeDashboard: ActiveDashboard,
): Selection {
  const { organization, team } = activeDashboard;
  if (organization && team) {
    const org = memberships.find((o) => o.id === organization);
    if (org?.teams.some((t) => t.id === team)) {
      return { orgId: organization, teamId: team };
    }
  }

  for (const org of memberships) {
    const firstTeam = org.teams[0];
    if (firstTeam) return { orgId: org.id, teamId: firstTeam.id };
  }
  return { orgId: memberships[0]?.id ?? '', teamId: '' };
}

function SelectTeamPage() {
  const { workspaces } = Route.useRouteContext();

  switch (workspaces.status) {
    case 'lookup-failed':
      return <WorkspaceLookupFailed />;
    case 'no-teams':
      return <NoTeamsPage />;
    case 'choose':
      return <SelectTeamForm memberships={workspaces.memberships} />;
  }
}

function SelectTeamForm({
  memberships,
}: {
  memberships: MembershipOrganization[];
}) {
  const { t } = useLingui();
  const { env, activeDashboard } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selection, setSelection] = useState<Selection>(() =>
    defaultSelection(memberships, activeDashboard),
  );

  const mutation = useMutation<
    undefined,
    Error,
    { orgId: string; teamId: string }
  >({
    mutationFn: async ({ orgId, teamId }) => {
      // Calls the server function directly — no BFF HTTP round-trip.
      // TanStack Start serialises the call across the SSR boundary and the
      // handler runs in the same Node process as the rest of the app.
      const result = await persistActiveDashboard({
        data: { organizationId: orgId, teamId },
      });
      if (!result.success) {
        throw new Error(
          failureMessage(result.reason, t`Failed to update active team`),
        );
      }

      // `persistActiveDashboard` also remembers the choice in the
      // `last_dashboard` cookie, so the next login resumes it without asking.
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.session.current(),
      });
      const destination =
        getSafeReturnTo(search.returnTo) ?? `${BASE_PATH}/researchers/projects`;

      // A document load because `returnTo` is any same-origin path, including
      // the legacy app outside BASE_PATH, which this router cannot resolve.
      // Absolute URL because `href` only accepts a complete URL — given a
      // path, router-core discards it and reloads the current route instead.
      await navigate({
        href: new URL(destination, globalThis.location.origin).href,
        reloadDocument: true,
      });
    },
  });

  const onSubmit = () => {
    if (!selection.orgId || !selection.teamId || mutation.isPending) return;
    mutation.mutate(selection);
  };

  const selectedOrg =
    memberships.find((org) => org.id === selection.orgId) ?? memberships[0];
  const teams = selectedOrg?.teams ?? [];

  const onSelectOrganization = (orgId: string) => {
    const org = memberships.find((o) => o.id === orgId);
    setSelection({ orgId, teamId: org?.teams[0]?.id ?? '' });
  };

  return (
    <AuthLayout rightPanel={<ResearcherRightPanel />}>
      <div className="flex flex-col gap-8">
        <div>
          <h1 className="font-heading text-display-xs font-semibold text-primary">
            <Trans>Choose your workspace</Trans>
          </h1>
          <p className="mt-1 text-md text-secondary">
            <Trans>
              Pick the organization and team you want to work in. You can switch
              any time from the sidebar.
            </Trans>
          </p>
        </div>

        {mutation.error && (
          <AlertFloating
            color="error"
            title={t`Couldn't switch team`}
            description={mutation.error.message}
            confirmLabel=""
          />
        )}

        <div className="flex flex-col gap-5">
          {memberships.length > 1 ? (
            <Select
              label={t`Organization`}
              placeholder={t`Select an organization`}
              size="md"
              value={selection.orgId}
              onSelectionChange={onSelectOrganization}
              items={memberships.map((org) => ({
                id: org.id,
                label: org.name,
                avatarUrl: org.imageUrl,
                supportingText:
                  org.teams.length === 1
                    ? t`1 team`
                    : t`${org.teams.length} teams`,
              }))}
            >
              {(item) => <Select.Item {...item} />}
            </Select>
          ) : (
            selectedOrg && (
              <p className="text-sm font-medium text-secondary">
                {selectedOrg.name}
              </p>
            )
          )}

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1.5 text-sm font-medium text-secondary">
              <Trans>Team</Trans>
            </legend>
            {teams.map((team) => (
              <TeamOption
                key={team.id}
                team={team}
                isSelected={selection.teamId === team.id}
                onSelect={(teamId) =>
                  setSelection((current) => ({ ...current, teamId }))
                }
              />
            ))}
          </fieldset>
        </div>

        {/*
          Joining another team, creating one, and approving other people's
          requests all still live in the legacy team switcher — none of it has
          been rebuilt here yet. Rather than leave those capabilities with no
          door at all in V3, point at the legacy screen. `teamselector=1` is
          what stops legacy bouncing straight past its switcher into the
          remembered dashboard.
        */}
        <ExternalLinkButton
          href={`${env.WEBSITE_URL}/dashboard?teamselector=1`}
          color="link-color"
          size="md"
          className="self-start"
        >
          <Trans>Join or create a team</Trans>
        </ExternalLinkButton>

        <div className="flex items-center justify-between">
          <LinkButton color="link-gray" size="md" to="/sign-out">
            <Trans>Sign out</Trans>
          </LinkButton>
          <Button
            color="primary"
            size="lg"
            isLoading={mutation.isPending}
            isDisabled={!selection.teamId || mutation.isPending}
            onClick={onSubmit}
          >
            <Trans>Continue</Trans>
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
}

function TeamOption({
  team,
  isSelected,
  onSelect,
}: {
  team: MembershipTeam;
  isSelected: boolean;
  onSelect: (teamId: string) => void;
}) {
  const inputId = `team-option-${team.id}`;
  return (
    <label
      htmlFor={inputId}
      className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
        isSelected
          ? 'border-brand-solid bg-brand-primary_alt'
          : 'border-secondary hover:bg-secondary_subtle'
      }`}
    >
      <input
        id={inputId}
        type="radio"
        name="active-team"
        className="accent-brand-solid size-4 cursor-pointer"
        checked={isSelected}
        onChange={() => onSelect(team.id)}
      />
      <span className="truncate text-md font-medium text-primary">
        {team.name}
      </span>
    </label>
  );
}

function WorkspaceLookupFailed() {
  const router = useRouter();
  const [isRetrying, setIsRetrying] = useState(false);

  return (
    <AuthLayout rightPanel={<ResearcherRightPanel />}>
      <div className="flex flex-col gap-8">
        <div>
          <h1 className="font-heading text-display-xs font-semibold text-primary">
            <Trans>We couldn&apos;t load your workspaces</Trans>
          </h1>
          <p className="mt-1 text-md text-secondary">
            <Trans>
              This is usually temporary. Try again, and if it keeps happening
              let us know.
            </Trans>
          </p>
        </div>

        <div className="flex items-center justify-between">
          <LinkButton color="link-gray" size="md" to="/researchers/sign-out">
            <Trans>Sign out</Trans>
          </LinkButton>
          <Button
            color="primary"
            size="lg"
            isLoading={isRetrying}
            onClick={() => {
              setIsRetrying(true);
              void router.invalidate().finally(() => setIsRetrying(false));
            }}
          >
            <Trans>Try again</Trans>
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
}

function NoTeamsPage() {
  const { env } = Route.useRouteContext();

  return (
    <AuthLayout rightPanel={<ResearcherRightPanel />}>
      <div className="flex flex-col gap-8">
        <div>
          <h1 className="font-heading text-display-xs font-semibold text-primary">
            <Trans>You&apos;re not on a team yet</Trans>
          </h1>
          <p className="mt-1 text-md text-secondary">
            <Trans>
              You belong to an organization, but none of its teams. An
              organization owner can add you to one — once they do, sign in
              again and you&apos;ll land straight in it.
            </Trans>
          </p>
        </div>

        <ExternalLinkButton
          href={`${env.WEBSITE_URL}/dashboard?teamselector=1`}
          color="link-color"
          size="md"
          className="self-start"
        >
          <Trans>Join or create a team</Trans>
        </ExternalLinkButton>

        <div className="flex items-center justify-between">
          <LinkButton color="link-gray" size="md" to="/researchers/sign-out">
            <Trans>Sign out</Trans>
          </LinkButton>
        </div>
      </div>
    </AuthLayout>
  );
}
